import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ErroApi } from '../lib/api';
import { escalar as enviarEscala } from '../lib/escalas';
import { assistirNoYouTube, capaDoYouTube } from '../lib/youtube';
import { dataPorExtenso, horaSP, iniciais, quandoEmPalavras } from '../lib/datas';
import { Alerta, Botao, Cartao } from '../componentes/Interface';
import { useConfirmacao } from '../componentes/Confirmacao';
import Layout from '../componentes/Layout';
import Repertorio from '../componentes/Repertorio';

type Musica = {
  id: number;
  musica: string;
  artista: string | null;
  tom: string | null;
  link: string | null;
  observacao: string | null;
};

type Participante = {
  id: number;
  membroId: number;
  nome: string;
  funcao: string;
  ministerio: string | null;
  status: string;
  justificativa: string | null;
};

type MinisterioComFuncoes = {
  id: number;
  nome: string;
  chave: string;
  funcoes: { id: number; nome: string }[];
  pessoas: { id: number; nome: string }[];
};

type Dados = {
  ministerios: MinisterioComFuncoes[];
  evento: {
    id: number;
    titulo: string;
    tipo: string;
    descricao: string | null;
    local: string | null;
    inicio: string | null;
  };
  minhaEscala: { id: number; funcao: string; status: string; observacao: string | null } | null;
  equipe: Participante[];
  repertorio: Musica[];
  podeEditar: boolean;
};

const CORES_STATUS: Record<string, string> = {
  convocado: 'bg-amber-100 text-amber-800',
  confirmado: 'bg-tinta-900 text-white',
  indisponivel: 'bg-red-100 text-red-700',
};

const NOME_STATUS: Record<string, string> = {
  convocado: 'aguardando',
  confirmado: 'confirmado',
  indisponivel: 'não pode',
};

export default function EscalaCulto() {
  const { id } = useParams();
  const navegar = useNavigate();
  const [dados, setDados] = useState<Dados | null>(null);
  // quem serve e a primeira pergunta de quem abre o culto, entao a equipe abre junto
  const [aba, setAba] = useState<'participantes' | 'detalhes' | 'musicas'>('participantes');
  const [erro, setErro] = useState<string | null>(null);
  const [respondendo, setRespondendo] = useState(false);
  const [justificando, setJustificando] = useState(false);
  const [justificativa, setJustificativa] = useState('');
  const [ocupado, setOcupado] = useState<string | null>(null);
  const { confirmar, dialogo } = useConfirmacao();

  const carregar = useCallback(async () => {
    try {
      const d = await api<Dados>(`/eventos/${id}/escala`);
      setDados(d);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui abrir este culto.');
    }
  }, [id]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function escalar(
    mi: MinisterioComFuncoes,
    funcao: { id: number; nome: string },
    membroId: number,
    confirmarMesmoAssim = false,
  ) {
    setOcupado(`escalar-${funcao.id}`);
    setErro(null);
    try {
      await enviarEscala({
        eventoId: Number(id),
        membroId,
        funcaoId: funcao.id,
        ministerioChave: mi.chave,
        ...(confirmarMesmoAssim ? { confirmarMesmoAssim: true } : {}),
      });
      await carregar();
    } catch (e) {
      // O servidor avisa da indisponibilidade sem decidir pelo lider (409 com
      // saida). Se a tela virasse esse aviso em erro seco, quem avisou ficaria
      // impossivel de escalar — justo no domingo em que se resolveu.
      if (e instanceof ErroApi && e.codigo === 'membro_indisponivel') {
        const ok = await confirmar({
          titulo: 'Essa pessoa avisou que não pode',
          texto: `${e.message} Escalar assim mesmo?`,
          confirmar: 'Escalar mesmo assim',
        });
        setOcupado(null);
        if (ok) await escalar(mi, funcao, membroId, true);
        return;
      }
      setErro(e instanceof ErroApi ? e.message : 'Não consegui escalar.');
    } finally {
      setOcupado(null);
    }
  }

  async function tirar(escalaId: number, nome: string) {
    const ok = await confirmar({
      titulo: `Tirar ${nome} desta escala?`,
      texto: 'A pessoa sai deste culto e some do calendário dela. Da para escalar de novo depois.',
      confirmar: 'Tirar da escala',
      perigo: true,
    });
    if (!ok) return;
    setOcupado(`tirar-${escalaId}`);
    try {
      await api(`/escalas/${escalaId}`, { method: 'DELETE' });
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui tirar da escala.');
    } finally {
      setOcupado(null);
    }
  }

  async function reporFuncoes(mi: MinisterioComFuncoes) {
    setOcupado(`padrao-${mi.id}`);
    setErro(null);
    try {
      await api(`/ministerios/${mi.id}/funcoes/padrao`, { method: 'POST' });
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui repor as funções.');
    } finally {
      setOcupado(null);
    }
  }

  async function responder(status: 'confirmado' | 'indisponivel', texto?: string) {
    if (!dados?.minhaEscala) return;
    setRespondendo(true);
    try {
      await api(`/escalas/${dados.minhaEscala.id}/responder`, {
        method: 'POST',
        body: JSON.stringify({ status, ...(texto ? { justificativa: texto } : {}) }),
      });
      setJustificando(false);
      setJustificativa('');
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui responder.');
    } finally {
      setRespondendo(false);
    }
  }

  if (erro && !dados) {
    return (
      <Layout titulo="Culto" subtitulo="">
        <div className="max-w-xl space-y-3">
          <Alerta>{erro}</Alerta>
          <Botao variante="secundario" onClick={() => navegar('/minhas-escalas')}>
            Voltar
          </Botao>
        </div>
      </Layout>
    );
  }

  if (!dados) {
    return (
      <Layout titulo="Culto" subtitulo="Carregando...">
        <span />
      </Layout>
    );
  }

  const { evento, equipe, repertorio, minhaEscala } = dados;
  const ministerios = dados.ministerios ?? [];
  const confirmados = equipe.filter((p) => p.status === 'confirmado').length;

  return (
    <Layout
      titulo={evento.titulo}
      subtitulo={
        evento.inicio
          ? `${dataPorExtenso(evento.inicio)} · ${horaSP(evento.inicio)} · ${quandoEmPalavras(evento.inicio)}`
          : ''
      }
      acoes={
        <Link
          to="/minhas-escalas"
          className="rounded-xl border border-tinta-200 px-3.5 py-2 text-sm font-semibold text-tinta-700 hover:bg-tinta-100"
        >
          Voltar
        </Link>
      }
    >
      {erro && (
        <div className="mb-4 max-w-2xl">
          <Alerta>{erro}</Alerta>
        </div>
      )}

      <div className="mb-4 grid max-w-3xl grid-cols-3 gap-3">
        {[
          { rotulo: 'Músicas', valor: repertorio.length },
          { rotulo: 'Participantes', valor: `${confirmados}/${equipe.length}` },
          { rotulo: 'Local', valor: evento.local ?? '—' },
        ].map((c) => (
          <Cartao key={c.rotulo} className="py-3 text-center">
            <span className="block text-lg font-bold text-tinta-900">{c.valor}</span>
            <span className="block text-xs text-tinta-400">{c.rotulo}</span>
          </Cartao>
        ))}
      </div>

      <div className="mb-4 flex max-w-lg rounded-2xl bg-white p-1 shadow-cartao">
        {(
          [
            { valor: 'participantes', rotulo: `Equipe (${equipe.length})` },
            { valor: 'detalhes', rotulo: 'Detalhes' },
            { valor: 'musicas', rotulo: `Musicas (${repertorio.length})` },
          ] as const
        ).map((a) => (
          <button
            key={a.valor}
            onClick={() => setAba(a.valor)}
            className={`flex-1 rounded-xl px-3 py-2 text-sm font-semibold transition ${
              aba === a.valor ? 'bg-tinta-900 text-white' : 'text-tinta-500 hover:bg-tinta-100'
            }`}
          >
            {a.rotulo}
          </button>
        ))}
      </div>

      <div className="max-w-3xl space-y-4">
        {aba === 'detalhes' && (
          <>
            {minhaEscala ? (
              <Cartao>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm text-tinta-400">Sua função neste culto</p>
                    <p className="text-lg font-bold text-tinta-900">{minhaEscala.funcao}</p>
                    {minhaEscala.observacao && (
                      <p className="mt-1 text-sm text-tinta-600">{minhaEscala.observacao}</p>
                    )}
                  </div>
                  <span
                    className={`rounded-lg px-3 py-1 text-sm font-semibold ${
                      CORES_STATUS[minhaEscala.status] ?? ''
                    }`}
                  >
                    {NOME_STATUS[minhaEscala.status] ?? minhaEscala.status}
                  </span>
                </div>

                {justificando ? (
                  <div className="mt-4 space-y-2">
                    <input
                      autoFocus
                      value={justificativa}
                      onChange={(e) => setJustificativa(e.target.value)}
                      placeholder="Conte o motivo (opcional)"
                      className="w-full rounded-xl border border-tinta-200 px-3 py-2 text-sm"
                    />
                    <div className="flex gap-2">
                      <Botao
                        carregando={respondendo}
                        onClick={() => void responder('indisponivel', justificativa.trim() || undefined)}
                      >
                        Avisar que nao posso
                      </Botao>
                      <Botao variante="secundario" onClick={() => setJustificando(false)}>
                        Cancelar
                      </Botao>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {minhaEscala.status !== 'confirmado' && (
                      <Botao carregando={respondendo} onClick={() => void responder('confirmado')}>
                        Confirmar presenca
                      </Botao>
                    )}
                    {minhaEscala.status !== 'indisponivel' && (
                      <Botao variante="secundario" onClick={() => setJustificando(true)}>
                        Nao vou poder
                      </Botao>
                    )}
                  </div>
                )}
              </Cartao>
            ) : (
              <Cartao>
                <p className="text-sm text-tinta-400">
                  Voce nao esta escalado neste culto — esta vendo como equipe.
                </p>
              </Cartao>
            )}

            {evento.descricao && (
              <Cartao>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-tinta-400">
                  Observacoes
                </h3>
                <p className="mt-2 whitespace-pre-line text-sm text-tinta-700">{evento.descricao}</p>
              </Cartao>
            )}
          </>
        )}

        {aba === 'musicas' && (
          <>
            {dados.podeEditar && <Repertorio eventoId={Number(id)} />}
            {repertorio.length === 0 ? (
              <Cartao>
                <p className="text-sm text-tinta-400">Nenhuma música escolhida ainda.</p>
              </Cartao>
            ) : (
              <ol className="space-y-3">
                {repertorio.map((m, i) => {
                  const capa = capaDoYouTube(m.link);
                  const assistir = assistirNoYouTube(m.link);
                  return (
                    <li key={m.id} className="flex gap-3 rounded-2xl bg-white p-3 shadow-cartao">
                      {capa ? (
                        <a
                          href={assistir ?? '#'}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="relative block h-16 w-28 shrink-0 overflow-hidden rounded-xl bg-tinta-100"
                        >
                          <img
                            src={capa}
                            alt=""
                            loading="lazy"
                            className="h-full w-full object-cover"
                          />
                          <span className="absolute inset-0 flex items-center justify-center bg-tinta-900/25 text-lg text-white">
                            ▶
                          </span>
                        </a>
                      ) : (
                        <span className="flex h-16 w-28 shrink-0 items-center justify-center rounded-xl bg-tinta-100 text-xl text-tinta-400">
                          ♪
                        </span>
                      )}

                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold text-tinta-900">
                          <span className="text-tinta-400">{i + 1}. </span>
                          {m.musica}
                        </p>
                        <p className="truncate text-sm text-tinta-400">
                          {m.artista ?? 'Sem artista'}
                          {m.tom ? ` · Tom ${m.tom}` : ''}
                        </p>
                        {m.observacao && (
                          <p className="mt-1 truncate text-xs text-tinta-400">{m.observacao}</p>
                        )}
                        {assistir && (
                          <a
                            href={assistir}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            className="mt-1 inline-block text-xs font-semibold text-tinta-900 hover:underline"
                          >
                            assistir no YouTube
                          </a>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </>
        )}

        {aba === 'participantes' && (
          <>
            {dados.podeEditar &&
              ministerios.map((mi) => {
                const doMinisterio = equipe.filter((p) => p.ministerio === mi.nome);
                return (
                  <Cartao key={mi.id}>
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <h3 className="font-semibold text-tinta-900">{mi.nome}</h3>
                      <span className="text-xs text-tinta-400">
                        {doMinisterio.filter((p) => p.status === 'confirmado').length}/
                        {doMinisterio.length} confirmados
                      </span>
                    </div>

                    {mi.funcoes.length === 0 ? (
                      <div className="rounded-xl bg-amber-50 p-3 text-sm text-amber-800 ring-1 ring-amber-100">
                        <p>
                          Este ministério está <b>sem nenhuma função</b>, então não há onde encaixar
                          as pessoas. Reponha as funcoes de sempre para voltar a escalar.
                        </p>
                        <Botao
                          className="mt-3"
                          carregando={ocupado === `padrao-${mi.id}`}
                          onClick={() => void reporFuncoes(mi)}
                        >
                          Restaurar funcoes do {mi.nome}
                        </Botao>
                      </div>
                    ) : mi.pessoas.length === 0 ? (
                      <p className="text-sm text-tinta-400">
                        Ninguem cadastrado no {mi.nome} ainda. Cadastre em Escalas, no bloco
                        "Pessoas do {mi.nome}".
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {mi.funcoes.map((f) => {
                          const naFuncao = doMinisterio.filter((p) => p.funcao === f.nome);
                          const livres = mi.pessoas.filter(
                            (p) => !naFuncao.some((e) => e.membroId === p.id),
                          );
                          return (
                            <div key={f.id} className="border-b border-tinta-200 pb-3 last:border-0">
                              <div className="flex items-center justify-between gap-3">
                                <span className="text-sm font-medium text-tinta-800">{f.nome}</span>
                                {livres.length > 0 && (
                                  <select
                                    value=""
                                    disabled={ocupado !== null}
                                    onChange={(ev) => {
                                      const membroId = Number(ev.target.value);
                                      ev.target.value = '';
                                      if (membroId) void escalar(mi, f, membroId);
                                    }}
                                    className="rounded-lg border border-tinta-200 bg-white px-2 py-1 text-sm text-tinta-600"
                                  >
                                    <option value="">+ escalar</option>
                                    {livres.map((p) => (
                                      <option key={p.id} value={p.id}>
                                        {p.nome}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </div>

                              {naFuncao.length === 0 ? (
                                <p className="mt-1 text-xs text-tinta-400">Ninguém escalado</p>
                              ) : (
                                <ul className="mt-2 space-y-1">
                                  {naFuncao.map((p) => (
                                    <li
                                      key={p.id}
                                      className="flex items-center gap-2 rounded-lg bg-tinta-100 px-3 py-1.5 text-sm"
                                    >
                                      <span className="truncate text-tinta-800">{p.nome}</span>
                                      <span
                                        className={`ml-auto shrink-0 rounded-md px-2 py-0.5 text-xs font-semibold ${
                                          CORES_STATUS[p.status] ?? ''
                                        }`}
                                      >
                                        {NOME_STATUS[p.status] ?? p.status}
                                      </span>
                                      <button
                                        onClick={() => void tirar(p.id, p.nome)}
                                        className="shrink-0 text-xs text-tinta-400 hover:text-red-600"
                                      >
                                        tirar
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Cartao>
                );
              })}

            <Cartao className="p-0">
              <ul className="divide-y divide-tinta-200">
                {equipe.map((p, i) => (
                  <li key={p.id} className="flex items-center gap-3 px-4 py-3">
                    <span
                      className={`flex h-9 w-9 items-center justify-center rounded-full text-[11px] font-bold text-white ${
                        ['bg-tinta-400', 'bg-tinta-900', 'bg-amber-600', 'bg-sky-700', 'bg-violet-700'][
                          i % 5
                        ]
                      } ${p.status === 'indisponivel' ? 'opacity-40' : ''}`}
                    >
                      {iniciais(p.nome)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-tinta-900">{p.nome}</span>
                      <span className="block truncate text-xs text-tinta-400">
                        {p.funcao}
                        {p.ministerio ? ` · ${p.ministerio}` : ''}
                        {p.justificativa ? ` · ${p.justificativa}` : ''}
                      </span>
                    </span>
                    <span
                      className={`shrink-0 rounded-md px-2 py-0.5 text-xs font-semibold ${
                        CORES_STATUS[p.status] ?? ''
                      }`}
                    >
                      {NOME_STATUS[p.status] ?? p.status}
                    </span>
                  </li>
                ))}
                {equipe.length === 0 && (
                  <li className="px-4 py-6 text-center text-sm text-tinta-400">
                    Ninguem escalado ainda.
                  </li>
                )}
              </ul>
            </Cartao>
          </>
        )}

      </div>
      {dialogo}
    </Layout>
  );
}
